import { useEffect, useMemo, useRef, useState } from 'react'
import { LensId, builderPrices } from '../data/builder'
import type { QuickAddId } from '../data/product'
import {
  BuildState,
  buildAttributes,
  buildCartUrl,
  buildLines,
  buildTotal,
  completeHint,
  initialBuild,
  missingLinks,
  variantIds,
} from '../utils/builderCart'
import { carryToSingle, requestOffer } from '../utils/offerBus'
import { trackCustom, trackEvent, withAttribution, CustomEventName } from '../utils/tracking'

export const quickAddPrices: Record<QuickAddId, number> = {
  fullLens: builderPrices.fullLensSystem,
  hardCase: builderPrices.hardCase,
  careKit: builderPrices.careKit,
  rx: builderPrices.rxInsert,
  lifetime: builderPrices.lifetimeProtection,
}

const addEvents: Record<QuickAddId, [CustomEventName, CustomEventName]> = {
  fullLens: ['full_lens_added', 'full_lens_removed'],
  hardCase: ['hard_case_added', 'hard_case_removed'],
  careKit: ['care_kit_added', 'care_kit_removed'],
  rx: ['rx_added', 'rx_quantity'],
  lifetime: ['lifetime_protection_selected', 'basic_protection_selected'],
}

/** Pause so the customer sees the "better deal" notice before being taken to the Complete System. */
const SEND_DELAY_MS = 1400

/**
 * The build under the "What's in the box" table. Uses the same Shopify products as the
 * builder. Once the core build (X1 + lenses + case) reaches $49.95 or more, the customer
 * is taken to the $49.95 Complete System card, with their Care Kit, RX and Lifetime carried over.
 */
export function useQuickBuild() {
  const [state, setState] = useState<BuildState>(initialBuild)
  const [routing, setRouting] = useState(false)
  const [notConnected, setNotConnected] = useState(false)
  const [sending, setSending] = useState(false)
  const sentOnce = useRef(false)
  const timer = useRef<number>(0)

  const lines = useMemo(() => buildLines(state), [state])
  const total = useMemo(() => buildTotal(lines), [lines])
  const hint = useMemo(() => completeHint(state), [state])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  // Back from Shopify via the browser's back button: re-enable the buttons.
  useEffect(() => {
    const onShow = (event: PageTransitionEvent) => {
      if (event.persisted) setRouting(false)
    }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  const sendToComplete = (current: BuildState, auto: boolean) => {
    window.clearTimeout(timer.current)
    setSending(false)
    sentOnce.current = true
    trackCustom('complete_offer_accepted', { from_total: buildTotal(buildLines(current)), source: 'gear_table', auto })
    carryToSingle({ rx: current.rx, care: current.careKit ? 1 : 0, lifetime: current.protection === 'lifetime' })
    requestOffer('single')
  }

  const isAdded = (id: QuickAddId) => {
    if (id === 'fullLens') return state.fullLens
    if (id === 'hardCase') return state.hardCase
    if (id === 'careKit') return state.careKit
    if (id === 'rx') return state.rx > 0
    return state.protection === 'lifetime'
  }

  const toggle = (id: QuickAddId) => {
    const on = !isAdded(id)
    const [added, removed] = addEvents[id]
    trackCustom(on ? added : removed, { source: 'gear_table' })
    setNotConnected(false)
    const next: BuildState = { ...state }
    if (id === 'fullLens') next.fullLens = on
    if (id === 'hardCase') next.hardCase = on
    if (id === 'careKit') next.careKit = on
    if (id === 'rx') next.rx = on ? 1 : 0
    if (id === 'lifetime') next.protection = on ? 'lifetime' : 'basic'
    setState(next)

    // Reached $49.95 or more in core parts: take them to the Complete System (once).
    const nextHint = completeHint(next)
    window.clearTimeout(timer.current)
    if (nextHint.kind === 'better' && !sentOnce.current) {
      trackCustom('complete_offer_shown', { level: 'better', total: buildTotal(buildLines(next)), source: 'gear_table' })
      setSending(true)
      timer.current = window.setTimeout(() => sendToComplete(next, true), SEND_DELAY_MS)
    } else {
      setSending(false)
    }
  }

  const stay = () => {
    window.clearTimeout(timer.current)
    setSending(false)
    sentOnce.current = true
    trackCustom('complete_offer_declined', { source: 'gear_table' })
  }

  const setLens = (lens: LensId) => {
    trackCustom(`primary_lens_${lens}` as const, { source: 'gear_table' })
    setState((current) => ({ ...current, lens }))
  }

  const checkout = () => {
    const href = buildCartUrl(lines, buildAttributes(state, total))
    const params = {
      value: total,
      currency: 'USD',
      configuration_type: 'essential',
      primary_lens: state.lens,
      add_ons: lines.slice(1).map((line) => line.label),
      rx_quantity: state.rx,
      x1_quantity: 1,
      source: 'gear_table',
    }
    trackCustom('checkout_started', params)
    if (!href) {
      setNotConnected(true)
      trackCustom('builder_not_connected', { missing: missingLinks(lines), source: 'gear_table' })
      return
    }
    setRouting(true)
    trackEvent('AddToCart', {
      ...params,
      content_name: 'GritVue X1 Essential (built)',
      content_ids: variantIds(lines),
      content_type: 'product',
      num_items: lines.reduce((sum, line) => sum + line.quantity, 0),
    })
    const target = withAttribution(href)
    window.setTimeout(() => {
      window.location.href = target
    }, 350)
  }

  return {
    state,
    lines,
    total,
    hint,
    routing,
    notConnected,
    sending,
    isAdded,
    toggle,
    setLens,
    checkout,
    stay,
    goToComplete: () => sendToComplete(state, false),
  }
}

export type QuickBuildApi = ReturnType<typeof useQuickBuild>
